import React, { useState } from 'react';
import {
    Box, Card, CardContent, Divider, Typography,
    Tabs, Tab, Button, Collapse, IconButton, useTheme, useMediaQuery
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DataSelector from './DataSelector';
import DateSelector from './DateSelector';
import StorageSelect from './StorageSelect';
import Benchmark from './Benchmark';
import SearchIcon from '@mui/icons-material/Search';
import { SelectedMetrics, StorageLocation } from '../../Types/AnalyticsGraph';
import PresetData from './PresetData';
import BenchmarkSettings from '../../Types/BenchmarkSettings';

interface ControlPanelProps {
    isMobile: boolean;
    selectedMetrics: SelectedMetrics;
    getMetricInfo: (value: string) => { label: string; color: string };
    setSelectedMetrics: (metrics: SelectedMetrics) => void;
    submitDataFetch: (timeData: object, selectedMetrics: SelectedMetrics, selectedStorage: StorageLocation) => Promise<void>;
    selectedStorage: StorageLocation;
    setSelectedStorage: (storage: StorageLocation) => void;
    benchmarkSettings: BenchmarkSettings;
    setBenchmarkSettings: (settings: BenchmarkSettings) => void;
}
interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`control-panel-tabpanel-${index}`}
            aria-labelledby={`control-panel-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ pt: 3, pb: 2 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `control-panel-tab-${index}`,
        'aria-controls': `control-panel-tabpanel-${index}`,
    };
}

function ControlPanel({ isMobile, selectedMetrics, getMetricInfo, setSelectedMetrics, submitDataFetch,
    setSelectedStorage, selectedStorage, benchmarkSettings, setBenchmarkSettings }: ControlPanelProps) {

    const [currentTab, setCurrentTab] = useState(0);
    const [expanded, setExpanded] = useState(isMobile); // Default to collapsed on mobile, expanded on desktop

    const initialSelectedRange = 30; // Default to 30 days (1 Month from TIME_RANGES in DateSelector)
    const initialStartDate = new Date(Date.now() - initialSelectedRange * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    const [startDate, setStartDate] = useState<string>(initialStartDate);
    const [selectedRange, setSelectedRange] = useState<number>(initialSelectedRange);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
    };

    const calculateEndDate = (): Date => {
        const start = new Date(startDate);
        const end = new Date(start.getTime() + selectedRange * 24 * 60 * 60 * 1000);
        return end;
    };

    const checkIsValidDateRange = () => {
        const start = new Date(startDate);
        const end = calculateEndDate();
        const today = new Date();
        if (isNaN(start.getTime())) return false; // Check if startDate is a valid date
        return start <= today && start.getTime() < end.getTime();
    };
    const isValidDateRange = checkIsValidDateRange();

    const handleFetchData = () => {
        if (!isValidDateRange) return;

        const start = new Date(startDate);
        const end = calculateEndDate();

        const timeData = {
            startDate: start.toISOString(),
            endDate: end.toISOString(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };
        submitDataFetch(timeData, selectedMetrics, selectedStorage);
    };

    const handleBenchmarkSettingsChange = (settings: any) => {
        setBenchmarkSettings(settings);

    };

    return (<>
        <Box>
            <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={expanded ? 2 : 0}>
                        <Typography variant="h6" gutterBottom={expanded} sx={{ m: 0 }}>
                            Chart Controls
                        </Typography>
                        {isMobile && (
                            <IconButton onClick={() => setExpanded(!expanded)}>
                                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        )}
                    </Box>

                    <Collapse in={expanded || !isMobile}>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                            <Box sx={{ 
                                flexGrow: 1, 
                                borderRight: { xs: 0, md: 1 }, 
                                borderBottom: { xs: 1, md: 0 }, 
                                borderColor: 'divider', 
                                pr: { xs: 0, md: 2 },
                                pb: { xs: 2, md: 0 },
                                mb: { xs: 2, md: 0 }
                            }}>
                                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                                    <Tabs 
                                        value={currentTab} 
                                        onChange={handleTabChange} 
                                        aria-label="chart control tabs"
                                        variant={isMobile ? "scrollable" : "standard"}
                                        scrollButtons="auto"
                                        allowScrollButtonsMobile
                                    >
                                        <Tab label="Data & Storage" {...a11yProps(0)} />
                                        <Tab label="Benchmark" {...a11yProps(1)} />
                                        <Tab label="Advanced Options" {...a11yProps(2)} />
                                    </Tabs>
                                </Box>

                                <TabPanel value={currentTab} index={0}>
                                    <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
                                        Select Storage and Dataset
                                    </Typography>
                                    <StorageSelect
                                        isMobile={isMobile}
                                        setSelectedStorage={setSelectedStorage}
                                        selectedStorage={selectedStorage}
                                    />
                                    <PresetData 
                                        setSelectedMetrics={setSelectedMetrics} 
                                        selectedMetrics={selectedMetrics}
                                    />
                                </TabPanel>

                                <TabPanel value={currentTab} index={1}>
                                    <Benchmark
                                        onSettingsChange={handleBenchmarkSettingsChange}
                                        initialSettings={benchmarkSettings}
                                        selectedStorageId={selectedStorage.id}
                                    />
                                </TabPanel>

                                <TabPanel value={currentTab} index={2}>
                                    <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
                                        Manually Select Metrics
                                    </Typography>
                                    <Typography variant="subtitle2" gutterBottom>
                                        <DataSelector
                                            selectedMetrics={selectedMetrics}
                                            getMetricInfo={getMetricInfo}
                                            setSelectedMetrics={setSelectedMetrics}
                                        />
                                    </Typography>
                                </TabPanel>
                            </Box>

                            <Box sx={{ width: { xs: '100%', md: '300px' }, flexShrink: 0 }}>
                                <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
                                    Select Timeframe for Analysis
                                </Typography>
                                <DateSelector
                                    startDate={startDate}
                                    setStartDate={setStartDate}
                                    selectedRange={selectedRange}
                                    setSelectedRange={setSelectedRange}
                                    isValidDateRange={isValidDateRange}
                                />
                                <Box sx={{ mt: 3, mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="contained"
                                        onClick={handleFetchData}
                                        disabled={!isValidDateRange}
                                        startIcon={<SearchIcon />}
                                        size="large"
                                        fullWidth={isMobile}
                                    >
                                        Fetch Data
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                        <Divider sx={{ mb: 2, mt: { xs: 2, md: 0 } }} />
                    </Collapse>
                </CardContent>
            </Card>
        </Box>
    </>)
}

export default ControlPanel;